import React, { createContext, useContext, useEffect, useMemo, useState, ReactNode } from 'react';

/**
 * App Bridge Context
 * Provides access to the Shopify App Bridge instance loaded via CDN
 * All child components can use the useAppBridge() hook to access App Bridge
 */

// Minimal type definition for Shopify App Bridge global
// Full types are provided by @shopify/app-bridge-types package
// Note: The new App Bridge (App Home) does NOT have a redirect API.
// Use window.open(url, '_top') to break out of the iframe instead.
interface ShopifyGlobalType {
  idToken: () => Promise<string>;
  toast: {
    show: (options: { message: string; duration?: number; isError?: boolean }) => void;
  };
  // Add other methods as needed
}

interface ShopifyAppBridgeContextValue {
  shopify: ShopifyGlobalType | null;
  isReady: boolean;
  appBridgeError: string | null;
}

const ShopifyAppBridgeContext = createContext<ShopifyAppBridgeContextValue>({
  shopify: null,
  isReady: false,
  appBridgeError: null,
});

interface ShopifyAppBridgeProviderProps {
  children: ReactNode;
}

/**
 * Provider component that makes App Bridge available to all child components
 * Waits for the CDN script to load and initializes the global shopify object
 */
/** How long to wait for window.shopify to appear before surfacing an error (ms) */
const APP_BRIDGE_TIMEOUT_MS = 10_000;

export function ShopifyAppBridgeProvider({ children }: ShopifyAppBridgeProviderProps) {
  const [shopify, setShopify] = useState<ShopifyGlobalType | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [appBridgeError, setAppBridgeError] = useState<string | null>(null);

  useEffect(() => {
    // Wait for App Bridge to be available on window
    const checkAppBridge = () => {
      if (typeof window !== 'undefined' && window.shopify) {
        setShopify(window.shopify as ShopifyGlobalType);
        setIsReady(true);
        return true;
      }
      return false;
    };

    // Check immediately
    if (checkAppBridge()) {
      return;
    }

    // Flag set to true once App Bridge is found (or on cleanup), so the timeout
    // callback never sets an error after successful initialisation.
    let initialised = false;

    // Poll for App Bridge availability (CDN script loads asynchronously)
    const interval = setInterval(() => {
      if (checkAppBridge()) {
        initialised = true;
        clearInterval(interval);
        clearTimeout(timeout);
      }
    }, 100);

    // Give up after APP_BRIDGE_TIMEOUT_MS — likely not running inside Shopify admin
    // or the CDN script failed to load.
    const timeout = setTimeout(() => {
      if (initialised) return; // App Bridge arrived just before the timeout fired
      clearInterval(interval);
      setAppBridgeError(
        'Shopify App Bridge did not load within the expected time. ' +
        'Please ensure this page is opened inside the Shopify admin.'
      );
    }, APP_BRIDGE_TIMEOUT_MS);

    // Cleanup — mark as initialised so the timeout callback is a no-op if it
    // fires after unmount.
    return () => {
      initialised = true;
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, []);

  const value = useMemo(() => ({ shopify, isReady, appBridgeError }), [shopify, isReady, appBridgeError]);

  return (
    <ShopifyAppBridgeContext.Provider value={value}>
      {children}
    </ShopifyAppBridgeContext.Provider>
  );
}

/**
 * Hook to access App Bridge in functional components
 * Returns the shopify global object and ready state
 */
export function useAppBridge() {
  const context = useContext(ShopifyAppBridgeContext);
  if (!context) {
    throw new Error('useAppBridge must be used within ShopifyAppBridgeProvider');
  }
  return context;
}
